import { InfiniteData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/Dashboard'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import ApprovalIcon from '@mui/icons-material/Rule'
import ReportIcon from '@mui/icons-material/Assessment'
import AdminIcon from '@mui/icons-material/AdminPanelSettings'
import PeopleIcon from '@mui/icons-material/People'
import BusinessIcon from '@mui/icons-material/Business'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import DnsIcon from '@mui/icons-material/Dns'
import EventIcon from '@mui/icons-material/Event'
import UpdateIcon from '@mui/icons-material/SystemUpdateAlt'
import WorkspacesIcon from '@mui/icons-material/Workspaces'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import InsightsIcon from '@mui/icons-material/Insights'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { apiGet, apiPost, AppMeta, AppNotification, Me, NotificationSummary, PageResult } from '../api'

const drawerWidth = 280
const NOTIFICATION_POLL_INTERVAL_MS = 15 * 60 * 1000

type NavLinkItem = {
  type: 'link'
  label: string
  to: string
  icon: ReactNode
  roles?: Array<Me['role']>
}

type NavGroupItem = {
  type: 'group'
  label: string
  icon: ReactNode
  roles?: Array<Me['role']>
  children: NavLinkItem[]
}

type NavItem = NavLinkItem | NavGroupItem

function initialsFromEmail(email?: string): string {
  if (!email) {
    return 'U'
  }
  const local = email.split('@')[0]
  const parts = local.split(/[._-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
  }
  return local.slice(0, 2).toUpperCase()
}

function roleLabel(role?: Me['role']): string {
  switch (role) {
    case 'MIS_ENGINEER':
      return 'MIS Engineer'
    case 'MANAGER':
      return 'Manager'
    case 'SUPER_ADMIN':
      return 'Super Admin'
    case 'ADMIN':
      return 'Admin'
    default:
      return ''
  }
}

function canView(item: { roles?: Array<Me['role']> }, me?: Me): boolean {
  return !item.roles || (!!me && item.roles.includes(me.role))
}

function isRouteSelected(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname.startsWith(to)
}

function normalizeNotificationTarget(targetUrl?: string | null): string {
  if (!targetUrl) {
    return '/notifications'
  }
  if (targetUrl.startsWith('/forecast-projects')) {
    return targetUrl.replace('/forecast-projects', '/admin/forecasts')
  }
  return targetUrl
}


export default function AppShell({ title, me, onLogout, children }: { title: string; me?: Me; onLogout?: () => void; children?: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null)
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null)
  const [reportsExpanded, setReportsExpanded] = useState(false)
  const loc = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const metaQ = useQuery({
    queryKey: ['app-meta'],
    queryFn: () => apiGet<AppMeta>('/api/lookups/app-meta'),
    enabled: !!me,
    staleTime: 15 * 60 * 1000
  })

  const removeNotificationFromCache = (id: string) => {
    queryClient.setQueryData<NotificationSummary>(['notifications', 'summary'], (current) => {
      if (!current || current.unreadCount <= 0) {
        return current
      }
      return { unreadCount: current.unreadCount - 1 }
    })
    queryClient.setQueryData<PageResult<AppNotification>>(['notifications', 'menu'], (current) => {
      if (!current) {
        return current
      }
      return {
        ...current,
        items: current.items.filter((item) => item.id !== id)
      }
    })
    queryClient.setQueryData<InfiniteData<PageResult<AppNotification>>>(['notifications', 'page'], (current) => {
      if (!current) {
        return current
      }
      return {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => item.id !== id)
        }))
      }
    })
  }

  const summaryQ = useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: () => apiGet<NotificationSummary>('/api/notifications/summary'),
    enabled: !!me,
    refetchInterval: me ? NOTIFICATION_POLL_INTERVAL_MS : false,
    refetchOnMount: true
  })
  const notificationsQ = useQuery({
    queryKey: ['notifications', 'menu'],
    queryFn: async () => {
      const page = await apiGet<PageResult<AppNotification>>('/api/notifications?limit=8&offset=0')
      return { ...page, items: page.items.filter((item) => !item.read) }
    },
    enabled: !!me,
    refetchInterval: me ? NOTIFICATION_POLL_INTERVAL_MS : false,
    refetchOnMount: true
  })
  const markRead = useMutation({
    mutationFn: (id: string) => apiPost<void>(`/api/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] })
  })

  const items: NavItem[] = useMemo(() => [
    { type: 'link', label: 'Dashboard', to: '/', icon: <DashboardIcon /> },
    {
      type: 'group',
      label: 'Reports',
      icon: <ReportIcon />,
      roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'],
      children: [
        { type: 'link', label: 'Balance Report', to: '/reports/balance', icon: <ReportIcon />, roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
        { type: 'link', label: 'Revenue Report', to: '/reports/revenue', icon: <MonetizationOnIcon />, roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
        { type: 'link', label: 'Internal Revenue', to: '/reports/internal-revenue', icon: <MonetizationOnIcon />, roles: ['SUPER_ADMIN'] },
        { type: 'link', label: 'Time Tracking', to: '/reports/time-entries', icon: <AdminIcon />, roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
        { type: 'link', label: 'MIS Forecast', to: '/reports/mis-forecast', icon: <InsightsIcon />, roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
        { type: 'link', label: 'Team Utilization', to: '/reports/team-utilization', icon: <PeopleIcon />, roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] }
      ]
    },
    { type: 'link', label: 'My Time Entries', to: '/time', icon: <TimelapseIcon /> },
    { type: 'link', label: 'Approvals', to: '/approvals', icon: <ApprovalIcon />, roles: ['MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Customers', to: '/admin/customers', icon: <BusinessIcon />, roles: ['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Forecast Projects', to: '/admin/forecasts', icon: <InsightsIcon />, roles: ['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Maintenance', to: '/admin/maintenance-windows', icon: <EventIcon />, roles: ['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Patch Schedules', to: '/admin/patch-schedules', icon: <UpdateIcon />, roles: ['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'MIS Subscriptions', to: '/admin/mis-subscriptions', icon: <ReceiptLongIcon />, roles: ['ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Projects', to: '/admin/projects', icon: <WorkspacesIcon />, roles: ['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'MIS Packages', to: '/admin/mis-packages', icon: <WorkspacePremiumIcon />, roles: ['ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Clusters', to: '/admin/clusters', icon: <DnsIcon />, roles: ['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Users', to: '/admin/users', icon: <PeopleIcon />, roles: ['ADMIN', 'SUPER_ADMIN'] },
    { type: 'link', label: 'Health Settings', to: '/admin/customer-health', icon: <ReportIcon />, roles: ['ADMIN', 'SUPER_ADMIN'] }
  ], [])

  const visibleItems = useMemo<NavItem[]>(() => items.reduce<NavItem[]>((result, item) => {
    if (item.type === 'group') {
      if (!canView(item, me)) {
        return result
      }
      const visibleChildren = item.children.filter((child) => canView(child, me))
      if (visibleChildren.length === 0) {
        return result
      }
      result.push({ ...item, children: visibleChildren })
      return result
    }
    if (canView(item, me)) {
      result.push(item)
    }
    return result
  }, []), [items, me])

  const reportsGroup = visibleItems.find((item): item is NavGroupItem => item.type === 'group' && item.label === 'Reports')
  const reportsSelected = !!reportsGroup && reportsGroup.children.some((child) => isRouteSelected(loc.pathname, child.to))

  useEffect(() => {
    if (reportsSelected) {
      setReportsExpanded(true)
    }
  }, [reportsSelected])

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: 'primary.main', color: 'common.white' }}>
      <Toolbar sx={{ gap: 1, flexShrink: 0 }}>
        <Box component="img" src="/fischer-logo-white.png" alt="Fischer Identity" sx={{ height: 28 }} />
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.5 }}>
        {visibleItems.map((item) => {
          if (item.type === 'group') {
            const expanded = item.label === 'Reports' ? reportsExpanded : false
            const selected = item.children.some((child) => isRouteSelected(loc.pathname, child.to))
            return (
              <Box key={item.label}>
                <ListItemButton
                  onClick={() => setReportsExpanded((value) => !value)}
                  selected={selected}
                  sx={{
                    color: 'inherit',
                    '& .MuiListItemIcon-root': { color: 'inherit' },
                    '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.16)' },
                    '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' }
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </ListItemButton>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {item.children.map((child) => {
                      const childSelected = isRouteSelected(loc.pathname, child.to)
                      return (
                        <ListItemButton
                          key={child.to}
                          component={RouterLink}
                          to={child.to}
                          selected={childSelected}
                          onClick={() => setMobileOpen(false)}
                          sx={{
                            pl: 5.5,
                            color: 'inherit',
                            '& .MuiListItemIcon-root': { color: 'inherit' },
                            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.16)' },
                            '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>{child.icon}</ListItemIcon>
                          <ListItemText primary={child.label} />
                        </ListItemButton>
                      )
                    })}
                  </List>
                </Collapse>
              </Box>
            )
          }

          const selected = isRouteSelected(loc.pathname, item.to)
          return (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={selected}
              onClick={() => setMobileOpen(false)}
              sx={{
                color: 'inherit',
                '& .MuiListItemIcon-root': { color: 'inherit' },
                '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.16)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' }
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          )
        })}
      </List>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minWidth: 0 }}>
      <CssBaseline />
      <AppBar position="fixed" color="primary" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
          {me ? (
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ display: { sm: 'none' } }}>
              <MenuIcon />
            </IconButton>
          ) : null}
          <Box component="img" src="/fischer-logo-white.png" alt="Fischer Identity" sx={{ height: { xs: 22, sm: 26 }, mr: { xs: 0.5, sm: 1 }, flexShrink: 0 }} />
          <Typography variant="h6" noWrap sx={{ flex: 1, minWidth: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{title}</Typography>
          {me ? (
            <IconButton color="inherit" onClick={(event) => setNotificationAnchor(event.currentTarget)} sx={{ flexShrink: 0 }}>
              <Badge badgeContent={summaryQ.data?.unreadCount ?? 0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          ) : null}
          {me ? (
            <IconButton color="inherit" onClick={(event) => setAccountAnchor(event.currentTarget)} sx={{ p: 0.5, flexShrink: 0 }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,255,255,0.18)', color: 'common.white', border: '1px solid rgba(255,255,255,0.35)', fontSize: '0.85rem', fontWeight: 600 }}>
                {initialsFromEmail(me.email)}
              </Avatar>
            </IconButton>
          ) : null}
        </Toolbar>
      </AppBar>
      {me ? (
        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 'min(100vw, 280px)', borderRight: 'none' } }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none' } }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      ) : null}
      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: 'hidden', p: { xs: 1.5, sm: 3 }, width: { sm: me ? `calc(100% - ${drawerWidth}px)` : '100%' } }}>
        <Toolbar />
        {children}
      </Box>
      <Menu
        anchorEl={notificationAnchor}
        open={!!notificationAnchor}
        onClose={() => setNotificationAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ minWidth: { xs: 280, sm: 320 }, maxWidth: { xs: 'calc(100vw - 32px)', sm: 360 } }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2">Notifications</Typography>
          </Box>
          <Divider />
          {(notificationsQ.data?.items ?? []).length === 0 ? (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">No unread notifications</Typography>
            </Box>
          ) : (notificationsQ.data?.items ?? []).map((item) => (
            <MenuItem
              key={item.id}
              onClick={() => {
                removeNotificationFromCache(item.id)
                markRead.mutate(item.id)
                setNotificationAnchor(null)
                navigate(normalizeNotificationTarget(item.targetUrl))
              }}
              sx={{ whiteSpace: 'normal', alignItems: 'flex-start', bgcolor: 'rgba(25, 118, 210, 0.06)' }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                <Typography variant="caption" color="text.secondary">{item.body}</Typography>
              </Box>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem onClick={() => { setNotificationAnchor(null); navigate('/notifications') }}>View all</MenuItem>
        </Box>
      </Menu>
      <Menu
        anchorEl={accountAnchor}
        open={!!accountAnchor}
        onClose={() => setAccountAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {me ? [
          <Box key="account-meta" sx={{ px: 2, py: 1.5, minWidth: 220 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{me.displayName}</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>{me.email}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{roleLabel(me.role)}</Typography>
          </Box>,
          <Divider key="account-divider" />,
          <Box key="account-version" sx={{ px: 2, py: 1.5, minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Portal Version</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{metaQ.data?.portalVersion ?? 'Loading...'}</Typography>
          </Box>,
          <MenuItem key="release-notes" onClick={() => { setAccountAnchor(null); navigate('/release-notes') }}>Release Notes</MenuItem>,
          <MenuItem key="signout" onClick={() => { setAccountAnchor(null); onLogout?.() }}>Sign out</MenuItem>
        ] : null}
      </Menu>
    </Box>
  )
}


